import { omit } from 'lodash';
import type React from 'react';

import {
  COMPOSITION_GET_DATA_V2,
  CompensationDimension,
  type CompositionV2DataQuery,
  type CompositionV2Filters,
  GET_TOP_N,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { useRoleSelectionLists } from '../../../filters/selectionList/useRoleSelectionList';
import type { PrimaryFilterId } from '../../../filters/types/filters';
import { getCustomSkillLevelId } from '../../../filters/utils/customSkillTaxonomyRegistry';
import { getTopNFilters } from '../../../filters/utils/get-top-n-filters';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getDim1 } from '../../../filters/utils/getDim1';
import { TOP_N_LIMIT } from '../constants';
import { getTopEntities } from '../TopNDataProvider';
import type { DataProviderQueryStatus } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';

export type DrilldownFilter =
  | {
      segment: 'geography';
      level: 'region' | 'country' | 'msa' | 'city';
      id: number;
    }
  | {
      segment: 'role';
      levelId:
        | 'role_k7'
        | 'role_k50'
        | 'role_k150'
        | 'role_k500'
        | 'role_k1500'
        | 'role_k5000'
        | 'role_k15000';
      id: string;
    }
  | {
      segment: 'rics';
      level: 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company';
      id: number;
    }
  | {
      segment: 'skills';
      level: 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
      id: string;
    };

const SEGMENT_TO_BREAKDOWN_KEY: Record<
  DrilldownFilter['segment'],
  'Geography' | 'Role' | 'Rics' | 'Skill'
> = {
  geography: 'Geography',
  role: 'Role',
  rics: 'Rics',
  skills: 'Skill',
};

function mergeDrilldownIntoFilters(
  base: CompositionV2Filters,
  drilldown: DrilldownFilter
): CompositionV2Filters {
  switch (drilldown.segment) {
    case 'geography':
      return {
        ...base,
        region: drilldown.level === 'region' ? [drilldown.id] : undefined,
        country: drilldown.level === 'country' ? [drilldown.id] : undefined,
        msa: drilldown.level === 'msa' ? [drilldown.id] : undefined,
        city: drilldown.level === 'city' ? [drilldown.id] : undefined,
      };
    case 'role':
      return {
        ...base,
        custom_role: {
          ...(base.custom_role ?? {}),
          entities: [{ id: drilldown.id, levelId: drilldown.levelId }],
        },
      };
    case 'rics':
      return {
        ...base,
        rics_k10: drilldown.level === 'rics_k10' ? [drilldown.id] : undefined,
        rics_k50: drilldown.level === 'rics_k50' ? [drilldown.id] : undefined,
        rics_k400:
          drilldown.level === 'rics_k400' || drilldown.level === 'company'
            ? [drilldown.id]
            : undefined,
      };
    case 'skills': {
      const entityId = parseInt(drilldown.id.split('_').slice(-1)[0] ?? '', 10);
      const levelId = getCustomSkillLevelId(drilldown.level);
      if (!Number.isFinite(entityId) || levelId === undefined) {
        return base;
      }
      return {
        ...base,
        custom_skill: {
          taxonomy_id: base.custom_skill?.taxonomy_id ?? 0,
          entities: [{ entity_id: entityId, level_id: levelId }],
        },
      };
    }
  }
}

type Props = {
  primaryDimension: PrimaryFilterId;
  drilldown: DrilldownFilter;
  children: (result: {
    data: CompositionV2DataQuery | undefined;
    status: DataProviderQueryStatus;
  }) => React.ReactNode;
};

export function CompositionTreemapDrilldownProvider({
  primaryDimension,
  drilldown,
  children,
}: Props) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListState();
  const roleSelectionLists = useRoleSelectionLists();
  const { customSkillTree } = useCustomSkillTaxonomyTree();
  const geographyMode = useGeographyMode();

  const selectionListsWithRole = {
    ...selectionLists,
    ...roleSelectionLists,
  };

  const baseVariables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    selectionLists: selectionListsWithRole,
    geographyMode,
    ...(drilldown.segment === 'skills' && {
      customSkillTree,
      customSkillSelections: [drilldown.id],
      customSkillDeepestLevel: drilldown.level,
    }),
  });

  const mergedFilters = mergeDrilldownIntoFilters(
    baseVariables.filters,
    drilldown
  );

  const breakdownKey = SEGMENT_TO_BREAKDOWN_KEY[drilldown.segment];
  const drilldownVariables = {
    ...baseVariables,
    filters: mergedFilters,
    breakdownOptions: {
      ...baseVariables.breakdownOptions,
      [breakdownKey]: { all: true },
    },
  };

  const isRicsCompanyBreakdown =
    drilldown.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company');

  const topNFilters = isRicsCompanyBreakdown
    ? {
        ...getTopNFilters(effectiveFilters),
        rics_k400: [drilldown.id],
      }
    : null;

  const dim1 = getDim1(primaryDimension);
  const [{ data: topNData, status: topNStatus }] = useUrqlQueryWithStatus({
    query: GET_TOP_N,
    variables: {
      filters: topNFilters ?? {},
      dim1: dim1 ?? undefined,
      dim2: CompensationDimension.Company,
    },
    pause: !isRicsCompanyBreakdown || dim1 === undefined,
  });

  const topEntities =
    isRicsCompanyBreakdown && topNData?.compensation
      ? getTopEntities({
          data: topNData.compensation,
          secondaryDimension: 'company',
          filters: topNFilters ?? {},
        })?.slice(0, TOP_N_LIMIT)
      : undefined;

  const compositionVariables = (() => {
    if (
      isRicsCompanyBreakdown &&
      topEntities != null &&
      topEntities.length > 0
    ) {
      const filtersWithoutRics = omit(drilldownVariables.filters, [
        'rics_k10',
        'rics_k50',
        'rics_k400',
      ]);
      return {
        ...drilldownVariables,
        filters: filtersWithoutRics,
        entities: topEntities,
      };
    }
    return drilldownVariables;
  })();

  const compositionPause =
    isRicsCompanyBreakdown && (!topEntities || topEntities.length === 0);

  const [{ data, status: compositionStatus }] = useUrqlQueryWithStatus({
    query: COMPOSITION_GET_DATA_V2,
    variables: compositionVariables,
    pause: compositionPause,
  });

  const status = (() => {
    if (isRicsCompanyBreakdown) {
      return combineQueryStatus([topNStatus, compositionStatus]);
    }
    return compositionStatus;
  })();

  const finalData = (() => {
    if (
      isRicsCompanyBreakdown &&
      (compositionPause ||
        (topNData != null && (!topEntities || topEntities.length === 0)))
    ) {
      return undefined;
    }
    return data;
  })();

  return <>{children({ data: finalData, status })}</>;
}
